import { useState } from 'react'
import useSWR from 'swr'
import { api } from '@/api'

interface Announcement {
  id: number
  title: string
  content: string
  is_active: boolean
  sort_order: number
}

export default function AdminAnnouncements() {
  const { data, mutate } = useSWR('/admin/announcements', () => api.announcements.getAll())
  const announcements = data?.data || []

  const [isEditing, setIsEditing] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null)
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    const formData = new FormData(e.target as HTMLFormElement)
    
    const announcementData = {
      title: formData.get('title') as string,
      content: formData.get('content') as string,
      sort_order: parseInt(formData.get('sort_order') as string) || 0,
      is_active: formData.get('is_active') === 'on',
    }

    try {
      if (editingAnnouncement) {
        await api.announcements.update(editingAnnouncement.id, announcementData)
      } else {
        await api.announcements.create(announcementData)
      }
      mutate()
      setIsEditing(false)
      setEditingAnnouncement(null)
    } catch (err: any) {
      alert('保存失败：' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('确定删除此公告吗？')) return
    try {
      await api.announcements.delete(id)
      mutate()
    } catch (err: any) {
      alert('删除失败：' + err.message)
    }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900">店面公告</h2>
        <button
          onClick={() => {
            setEditingAnnouncement(null)
            setIsEditing(true)
          }}
          className="btn-primary py-2 px-4 text-sm"
        >
          添加公告
        </button>
      </div>

      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <form onSubmit={handleSubmit} className="card w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold text-lg mb-4">
              {editingAnnouncement ? '编辑公告' : '添加公告'}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  公告标题 *
                </label>
                <input
                  name="title"
                  defaultValue={editingAnnouncement?.title}
                  className="input"
                  placeholder="输入公告标题"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  公告内容 *
                </label>
                <textarea
                  name="content"
                  defaultValue={editingAnnouncement?.content}
                  className="input resize-none"
                  rows={4}
                  placeholder="输入公告内容"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    排序
                  </label>
                  <input
                    name="sort_order"
                    type="number"
                    defaultValue={editingAnnouncement?.sort_order || 0}
                    className="input"
                    placeholder="0"
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2">
                    <input
                      name="is_active"
                      type="checkbox"
                      defaultChecked={editingAnnouncement?.is_active !== false}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">启用</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="btn-secondary flex-1"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {announcements.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            暂无公告
          </div>
        ) : (
          announcements.map(ann => (
            <div key={ann.id} className="card">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{ann.title}</h3>
                    {ann.is_active ? (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">启用</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">停用</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{ann.content}</p>
                  <p className="text-xs text-gray-500">排序：{ann.sort_order}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => {
                      setEditingAnnouncement(ann)
                      setIsEditing(true)
                    }}
                    className="text-sm text-primary-600"
                  >
                    编辑
                  </button>
                  <button
                    onClick={() => handleDelete(ann.id)}
                    className="text-sm text-red-600"
                  >
                    删除
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
